/**
 * DocumentItemsEditor.tsx — Dokumenty: EDYTOWALNY grid pozycji (increment 2).
 *
 * Klasowo-agnostyczny (wszystkie 7 klas dokumentów). Komplet pozycji jest stanem
 * kontrolowanym (`value`/`onChange`) podniesionym do strony detalu — przy zapisie
 * strona wysyła całą tablicę jako `items` w tym samym POST/PATCH co dokument
 * (replace-set; kwoty pochodne liczy serwer). Zero zapisów HTTP per komórka.
 *
 * Komórki są zawsze-edytowalne (input w każdej celi) — dla wprowadzania wielu
 * pozycji wygodniejsze niż click-to-edit. Stopka pokazuje PODGLĄD sum (klientowy,
 * przybliżony) — prawdą są kwoty przeliczone przez serwer po zapisie.
 *
 * Read-only widok tych samych danych: `DocumentItemsSection` (increment 1) —
 * strona wybiera edytor vs sekcję odczytową wg trybu edycji.
 *
 * Układ kolumn (`columnSettings` = `settings` elementu `document_items_view`): ten sam zapis
 * (`tableColumns` + maska `hiddenColumnIds`, a gdy ich brak — starszy `columnConfig`) steruje
 * odczytem i edycją. Edycja rysuje WSZYSTKIE kolumny pozycji (także te, których odczyt nie ma:
 * towar, kod, PKWiU, CN, GTIN) w kolejności układu, z jego widocznością, podpisem i wyrównaniem
 * (także tekstu w polach kolumny i wartości kontrolek list — jawne wyrównanie z układu; adnotacja
 * kolumny „tylko w edycji” jest widoczna wyłącznie w edytorze układu, nie w nagłówkach). Szerokość zapisana w układzie jest
 * szerokością STARTOWĄ; bez niej obowiązuje dotychczasowa szerokość domyślna. Miejsca po przecinku
 * (tylko prezentacja) dotyczą komórek WYLICZANYCH (wartość netto/VAT/brutto) — pola, w których
 * user wpisuje liczby, pokazują wartość surową. Kolumna „Akcje” nie należy do układu: zawsze stoi
 * na końcu. Filtr kolumny działa tylko dla kolumn widocznych (ukryta kolumna nie filtruje wierszy).
 *
 * Increment 3: SESYJNY resize kolumn (drag na uchwycie przy prawej
 * krawędzi `<th>`, szerokości w `<colgroup>`/`<col>`, NIE zapisywany — reset
 * po remouncie, nakłada się na szerokość startową z układu) + KLIENTOWY, NIEDESTRUKCYJNY filtr per kolumna (drugi wiersz
 * nagłówka pod tytułami; filtruje TYLKO które wiersze `value` są renderowane,
 * nigdy nie mutuje `value`). Numeracja Lp. i testidy komórek (`document-item-
 * *-<index>`) używają ORYGINALNEGO indeksu w `value`, nie pozycji w widoku
 * przefiltrowanym — Lp ma odzwierciedlać rzeczywistą kolejność na dokumencie.
 * `addRow` NIE czyści aktywnych filtrów (prostsze niż auto-reset) — nowy pusty
 * wiersz może być tymczasowo niewidoczny, jeśli filtr by go wykluczył; user
 * czyści filtr, aby go zobaczyć.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  effectiveColumnAlign,
  type EmbeddedTableColumnAlign,
  type ResolvedEmbeddedTableColumn,
  resolveEmbeddedTableColumns,
} from '@saas/shared';
import { Plus, Search, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { Dropdown } from '@/components/Dropdown';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { ExtFieldValueInput } from '@/components/system-modules/ExtFieldValueInput';
import { type ProductPickResult, ProductSearchPicker } from '@/components/ui/ProductSearchPicker';
import { useTabsOptional } from '@/contexts/TabContext';
import { useDictionary } from '@/hooks/useDictionary';
import {
  COLUMN_HEADER_KEYS,
  type DocumentItemColumnId as ColumnId,
  type DocumentLineItemDraft,
  extColumnSlug,
  isExtColumnId,
} from '@/lib/documents/document-items';
import {
  computeLinePreview,
  computeTotalsPreview,
  draftFromProduct,
  emptyDraft,
  normalizeDecimalInput,
} from '@/lib/documents/document-items-draft';
import { columnAlignClass, listControlValueAlignClass } from '@/lib/embedded-table/cells';
import {
  asDocumentItemColumnId,
  documentItemsLegacyReader,
} from '@/lib/embedded-table/document-items-descriptors';
import { useDocumentItemColumnsModel } from '@/lib/embedded-table/use-document-item-columns-model';
import { cn } from '@/lib/utils';

import { ProductPickerModal } from './ProductPickerModal';

export interface DocumentItemsEditorProps {
  value: DocumentLineItemDraft[];
  onChange: (next: DocumentLineItemDraft[]) => void;
  currencyCode: string | null | undefined;
  companyId: string | null | undefined;
  /** Klucze wierszy niekompletnych (patrz `validateItemsDraft`) — podświetlone na czerwono. */
  invalidRowKeys?: ReadonlySet<string>;
  /** Ustawienia elementu `document_items_view` z layoutu (`element.settings`): układ kolumn
   * (`tableColumns` + maska `hiddenColumnIds`), a gdy go brak — starszy `columnConfig`.
   * Brak ustawień → pełny zestaw kolumn w naturalnej kolejności, zero zmiany zachowania
   * względem stanu sprzed konfigurowalności. Kolumny wymagane i zablokowane (Lp., nazwa, ilość,
   * cena, VAT) są pokazane zawsze. */
  columnSettings?: Record<string, unknown>;
  'data-testid'?: string;
}

/** Stabilna referencja pustego zbioru — domyślna wartość `invalidRowKeys` (brak walidacji). */
const EMPTY_INVALID_ROW_KEYS: ReadonlySet<string> = new Set();

/**
 * Formatuje kwotę w walucie dokumentu. `currency` bywa dowolnym stringiem z bazy
 * (pole tekstowe, nie enum) — kod spoza ISO 4217 wywala `Intl.NumberFormat` z
 * `RangeError`, co bez przechwycenia ubijałoby render CAŁEGO edytora (jeden zły
 * wiersz danych = crash całej strony). Fallback: kwota + surowy kod waluty.
 * `decimals` (z układu kolumny) — miejsca po przecinku; brak = dotychczasowe 2.
 */
function formatMoney(value: number, currency: string, decimals?: number): string {
  const fractionDigits = decimals ?? 2;
  try {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(value);
  } catch {
    return `${value.toFixed(fractionDigits)} ${currency}`;
  }
}

// Wyrównanie jest dokładane per kolumna (`columnAlignClass(effectiveColumnAlign(col))`), więc
// bazowe klasy nagłówka i komórek nie niosą żadnego `text-*` wyrównania.
const thBase = 'px-2 py-2 text-xs font-semibold text-text-secondary whitespace-nowrap';
const td = 'px-2 py-1 align-top';
const tdComputed = 'px-2 py-1 align-top tabular-nums text-text-primary';
// Podświetlenie komórki listy (stawka VAT — `Dropdown` nie ma propa `invalid`) w wierszu
// niekompletnym (patrz `invalidRowKeys`): TA SAMA ramka co pole `Input` w stanie `invalid` (token
// błędu, bez dodatkowego pierścienia); pola `Input` używają własnego propa `invalid` z
// `hideStatusIcon` — sama czerwona ramka, bez ikony, która zabrałaby miejsce w wąskich kolumnach.
const invalidCellClass = 'border-[rgb(var(--color-error))]';

/**
 * Wyrównanie tekstu w WEWNĘTRZNYCH polach tekstowych/liczbowych pól dodatkowych (`<input>` i
 * `<textarea>` w `ExtFieldValueInput` — ich klas nie ustawiamy sami). Dokładane WYŁĄCZNIE przy
 * jawnym wyrównaniu z układu: bez niego pola wyglądają dokładnie jak dotąd. Pola list
 * (`select`/`multiselect`) tego selektora NIE dotyczy — ich wartość wyrównuje prop `valueAlign`
 * (przez `valueClassName` kontrolek), bo tekst wartości nie jest `<input>`em. Pełne stałe napisy —
 * Tailwind składa arkusz ze znalezionych w kodzie klas.
 */
const EXT_INPUT_ALIGN_CLASS: Record<EmbeddedTableColumnAlign, string> = {
  left: '[&_input]:text-left [&_textarea]:text-left',
  center: '[&_input]:text-center [&_textarea]:text-center',
  right: '[&_input]:text-right [&_textarea]:text-right',
};

/** Etykieta kolumny: własny podpis z układu albo etykieta deskryptora (wbudowana z i18n / nazwa pola ext). */
function columnLabelOf(col: ResolvedEmbeddedTableColumn): string {
  return col.label ?? col.descriptor.label;
}

/** Widoczna kolumna układu razem z identyfikatorem kolumny pozycji. */
interface EditorColumn {
  colId: ColumnId;
  col: ResolvedEmbeddedTableColumn;
}

/**
 * Domyślne szerokości (px) — używane, gdy układ nie ustawia szerokości kolumny. Szerokość z układu
 * jest STARTOWA, a przeciągnięcie uchwytu zmienia ją tylko w stanie sesji komponentu (zerowane po
 * remouncie). `Partial` (zamiast pełnego `Record`) — kolumny `ext:*` (Fala B3) nie mają tu
 * wpisu, dostają `FALLBACK_COLUMN_WIDTH` (patrz `widthOf`). Wpis `actions` to stała szerokość
 * kolumny usuwania wiersza, która nie należy do układu.
 */
const DEFAULT_COLUMN_WIDTHS: Partial<Record<ColumnId, number>> = {
  lineNumber: 48,
  product: 208,
  name: 208,
  productCode: 96,
  unit: 72,
  quantity: 88,
  unitPriceNet: 112,
  discountPercent: 88,
  vatRateCode: 96,
  pkwiu: 96,
  cn: 96,
  gtin: 112,
  valueNet: 112,
  valueVat: 112,
  valueGross: 112,
  actions: 56,
};

/** Minimalna szerokość kolumny podczas resize (px). */
const MIN_COLUMN_WIDTH = 48;

/** Szerokość domyślna dla kolumny bez wpisu w `DEFAULT_COLUMN_WIDTHS` — dziś dotyczy
 * kolumn pól dodatkowych (`ext:*`, Fala B3), które nie mają stałego wpisu w mapie. */
const FALLBACK_COLUMN_WIDTH = 160;

/** Szerokość kolumny usuwania wiersza — zawsze ostatniej, poza układem. */
const ACTIONS_COLUMN_WIDTH = DEFAULT_COLUMN_WIDTHS.actions ?? FALLBACK_COLUMN_WIDTH;

/**
 * Pola drafta filtrowane per kolumna (substring, case-insensitive). Kolumny kwotowe
 * (valueNet/valueVat/valueGross) są filtrowalne osobno — po WYŚWIETLANEJ kwocie podglądu
 * (patrz `visibleRows`), bo potrzebują waluty i przeliczenia, więc nie mieszczą się w tej
 * mapie prostych getterów drafta. Niefiltrowalne zostają tylko: `lineNumber` (auto-numer),
 * `product` (picker bez własnego tekstu — pokrywają go filtry Nazwa/Kod) i `actions`.
 */
const FILTER_FIELDS: Partial<Record<ColumnId, (row: DocumentLineItemDraft) => string>> = {
  name: (row) => row.name,
  productCode: (row) => row.productCode,
  unit: (row) => row.unit,
  quantity: (row) => row.quantity,
  unitPriceNet: (row) => row.unitPriceNet,
  discountPercent: (row) => row.discountPercent,
  vatRateCode: (row) => row.vatRateCode,
  pkwiu: (row) => row.pkwiu,
  cn: (row) => row.cn,
  gtin: (row) => row.gtin,
};

/** Kolumny kwotowe filtrowane po wyświetlanym podglądzie (currency-aware — patrz `visibleRows`). */
const AMOUNT_FILTER_COLUMNS: readonly ColumnId[] = ['valueNet', 'valueVat', 'valueGross'];

/** Czy kolumna ma wiersz filtra (getter drafta LUB kolumna kwotowa). */
function isFilterableColumn(colId: ColumnId): boolean {
  return Boolean(FILTER_FIELDS[colId]) || AMOUNT_FILTER_COLUMNS.includes(colId);
}

/**
 * Uchwyt resize przy prawej krawędzi `<th>` — kolumna akcji go nie ma (nie należy do układu).
 * Zdefiniowany na poziomie MODUŁU (nie wewnątrz `DocumentItemsEditor`) — funkcja-komponent
 * odtworzona przy KAŻDYM renderze rodzica miałaby inną tożsamość `type`, co zmusza Reacta
 * do odmontowania/zamontowania całego poddrzewa (i inputu filtra) na każde naciśnięcie
 * klawisza, gubiąc fokus w trakcie pisania.
 */
function ColumnResizeHandle({
  colId,
  width,
  isActive,
  label,
  onPointerDownStart,
}: {
  colId: ColumnId;
  /** Szerokość kolumny w chwili wciśnięcia uchwytu (sesyjna, zapisana w układzie albo domyślna). */
  width: number;
  isActive: boolean;
  label: string;
  onPointerDownStart: (
    e: React.PointerEvent<HTMLDivElement>,
    colId: ColumnId,
    startWidth: number,
  ) => void;
}) {
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      onPointerDown={(e) => onPointerDownStart(e, colId, width)}
      className={`absolute inset-y-0 right-0 w-1.5 cursor-col-resize touch-none select-none ${
        isActive ? 'bg-accent-500' : 'hover:bg-accent-400/50'
      }`}
      data-testid={`document-items-col-resize-${colId}`}
    />
  );
}

/** Input filtra klientowego pod tytułem kolumny — pusta `<th>` gdy kolumna nie jest filtrowalna. */
function ColumnFilterCell({
  colId,
  value,
  ariaLabel,
  onChangeValue,
}: {
  colId: ColumnId;
  value: string;
  ariaLabel: string;
  onChangeValue: (colId: ColumnId, value: string) => void;
}) {
  if (!isFilterableColumn(colId)) return <th className="px-2 py-1" scope="col" />;
  return (
    <th className="px-2 py-1" scope="col">
      {/* Lupa OBOK pola, nie w nim: pole `Input` zostaje z własnymi klasami rozmiaru `sm` — bez
          nadpisywania jego odstępów i czcionki (`Input` nie scala klas, więc kolizja utility
          zależałaby od kolejności w arkuszu). */}
      <div className="flex items-center gap-1">
        <Search className="h-3 w-3 shrink-0 text-text-muted" aria-hidden="true" />
        <Input
          size="sm"
          value={value}
          onChange={(e) => onChangeValue(colId, e.target.value)}
          aria-label={ariaLabel}
          data-testid={`document-items-filter-${colId}`}
        />
      </div>
    </th>
  );
}

export function DocumentItemsEditor({
  value,
  onChange,
  currencyCode,
  companyId,
  invalidRowKeys = EMPTY_INVALID_ROW_KEYS,
  columnSettings,
  'data-testid': testId = 'document-items-editor',
}: DocumentItemsEditorProps) {
  const { t } = useTranslation();
  const tabs = useTabsOptional();
  const currency = currencyCode || 'PLN';
  // Fala B3 — definicje pól dodatkowych (ext) modułu `document_items`. Pusta lista
  // (moduł jeszcze niezarejestrowany u tenanta / brak dodanych pól) → deskryptory tylko
  // wbudowane, zero zmiany zachowania względem stanu sprzed tej fali.
  const { descriptors, extFields: extFieldDefs } = useDocumentItemColumnsModel();
  const extFieldBySlug = useMemo(
    () => new Map(extFieldDefs.map((field) => [field.slug, field])),
    [extFieldDefs],
  );
  // Widoczne kolumny w kolejności układu; id spoza znanych kolumn pozycji (nie powinno się zdarzyć —
  // deskryptory są nasze) odpada jak dawniej osierocone id.
  const columns = useMemo<EditorColumn[]>(
    () =>
      resolveEmbeddedTableColumns(descriptors, columnSettings, {
        legacy: documentItemsLegacyReader,
      }).flatMap((col) => {
        const colId = asDocumentItemColumnId(col.id);
        return col.visible && colId !== undefined ? [{ colId, col }] : [];
      }),
    [descriptors, columnSettings],
  );
  const columnById = useMemo(
    () => new Map(columns.map(({ colId, col }) => [colId, col] as const)),
    [columns],
  );
  const vatDict = useDictionary('vat_rates');
  // Lista do <select> — TYLKO pozycje aktywne/selectable. Dopasowanie WARTOŚCI
  // liczbowej (przy wyborze towaru / zmianie stawki) czyta z PEŁNEJ listy
  // (`vatAllItems`) — stawka towaru albo wiersza wczytanego z serwera może
  // wskazywać kod, który w międzyczasie przestał być aktywny w słowniku
  // (nieaktywny/ukryty), a mimo to ma poprawną wartość liczbową do przeliczeń.
  const vatActiveItems = vatDict.activeItems ?? [];
  const vatAllItems = vatDict.items ?? [];

  const totals = useMemo(() => computeTotalsPreview(value), [value]);

  // ── F2: zaawansowany picker towaru (multi-add) ────────────────────────────
  // `key` wiersza, który otworzył modal — null gdy modal zamknięty. Jeden modal
  // dla całego edytora (nie per-wiersz), bo otwarty jest zawsze najwyżej jeden naraz.
  const [advancedPickerRowKey, setAdvancedPickerRowKey] = useState<string | null>(null);

  /** Zasada multi-add (F2, decyzja UX): 1 towar → zamienia produkt WYWOŁUJĄCEGO
   * wiersza (jak dotychczasowy pojedynczy wybór); >1 towarów → DOPISUJE tyle
   * nowych wierszy ile zaznaczono, wywołujący wiersz zostaje nietknięty. */
  function handlePickerConfirm(products: ProductPickResult[]) {
    if (advancedPickerRowKey === null || products.length === 0) {
      setAdvancedPickerRowKey(null);
      return;
    }
    const first = products[0];
    if (products.length === 1 && first) {
      applyProduct(advancedPickerRowKey, first);
    } else {
      const newRows = products.map((product) =>
        draftFromProduct(emptyDraft(), product, vatAllItems),
      );
      onChange([...value, ...newRows]);
    }
    // `setAdvancedPickerRowKey(null)` NIE jest tu potrzebne — modal woła `onClose`
    // (wpięte na `setAdvancedPickerRowKey(null)`) zaraz PO `onConfirm` (patrz
    // `ProductPickerModal.handleConfirm`), więc jedno miejsce czyszczące (onClose)
    // wystarcza (FIX E, przegląd F2).
  }

  // ── B: sesyjny resize kolumn ──────────────────────────────────────────────
  // Szerokości zmienione przeciąganiem w tej sesji — nakładają się na szerokość startową
  // (z układu albo domyślną) i NIE są zapisywane.
  const [sessionWidths, setSessionWidths] = useState<Partial<Record<ColumnId, number>>>({});
  const [resizingColumn, setResizingColumn] = useState<ColumnId | null>(null);
  const resizeStartRef = useRef<{ startX: number; startWidth: number }>({
    startX: 0,
    startWidth: 0,
  });

  /** Szerokość kolumny: sesyjna (przeciągnięta) → startowa z układu → domyślna → zapasowa (ext). */
  function widthOf({ colId, col }: EditorColumn): number {
    return (
      sessionWidths[colId] ?? col.width ?? DEFAULT_COLUMN_WIDTHS[colId] ?? FALLBACK_COLUMN_WIDTH
    );
  }

  function handleResizeStart(
    e: React.PointerEvent<HTMLDivElement>,
    colId: ColumnId,
    startWidth: number,
  ) {
    e.preventDefault();
    e.stopPropagation();
    resizeStartRef.current = { startX: e.clientX, startWidth };
    setResizingColumn(colId);
  }

  useEffect(() => {
    if (!resizingColumn) return;
    const activeColumn = resizingColumn;

    function handlePointerMove(e: PointerEvent) {
      const { startX, startWidth } = resizeStartRef.current;
      const nextWidth = Math.max(MIN_COLUMN_WIDTH, startWidth + (e.clientX - startX));
      setSessionWidths((prev) => ({ ...prev, [activeColumn]: nextWidth }));
    }
    function handlePointerUp() {
      setResizingColumn(null);
    }

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [resizingColumn]);

  // ── C: klientowy filtr per kolumna (niedestrukcyjny — patrz nagłówek pliku) ──
  const [filters, setFilters] = useState<Partial<Record<ColumnId, string>>>({});

  function setColumnFilter(colId: ColumnId, filterValue: string) {
    setFilters((prev) => ({ ...prev, [colId]: filterValue }));
  }

  // Kolumna, która przestała być widoczna (układ ją ukrył), traci swój filtr: bez tego wpisany filtr
  // wróciłby po cichu do działania, gdy kolumna znów się pojawi, a user nie widziałby, czemu brakuje
  // wierszy. Bez zmian zwraca ten sam obiekt (brak zbędnego renderu).
  useEffect(() => {
    setFilters((prev) => {
      const next: Partial<Record<ColumnId, string>> = {};
      let changed = false;
      for (const colId of Object.keys(prev) as ColumnId[]) {
        const filterValue = prev[colId];
        if (filterValue === undefined) continue;
        if (columnById.has(colId)) next[colId] = filterValue;
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [columnById]);

  const visibleRows = useMemo(() => {
    // Filtr kolumny, która przestała być widoczna (układ ją ukrył), nie filtruje wierszy —
    // user nie ma jak go zobaczyć ani wyczyścić.
    const activeFilters = (Object.entries(filters) as [ColumnId, string][]).filter(
      ([colId, filterValue]) => filterValue.trim() !== '' && columnById.has(colId),
    );
    return value
      .map((row, originalIndex) => ({ row, originalIndex }))
      .filter(({ row }) =>
        activeFilters.every(([colId, filterValue]) => {
          const query = filterValue.trim().toLowerCase();
          const getValue = FILTER_FIELDS[colId];
          if (getValue) return getValue(row).toLowerCase().includes(query);
          // Kolumny kwotowe: dopasowanie po WYŚWIETLANEJ kwocie podglądu (to, co user widzi —
          // z miejscami po przecinku z układu kolumny).
          if (colId === 'valueNet' || colId === 'valueVat' || colId === 'valueGross') {
            const preview = computeLinePreview(row);
            return formatMoney(preview[colId], currency, columnById.get(colId)?.decimals)
              .toLowerCase()
              .includes(query);
          }
          return true;
        }),
      );
  }, [value, filters, currency, columnById]);

  function updateRow(key: string, patch: Partial<DocumentLineItemDraft>) {
    onChange(value.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }

  function addRow() {
    onChange([...value, emptyDraft()]);
  }

  function removeRow(key: string) {
    onChange(value.filter((row) => row.key !== key));
  }

  /** Wybór towaru z kartoteki → kopiujemy MIGAWKI (wszystko dalej edytowalne).
   * Mapowanie pól/VAT wydzielone do `draftFromProduct` (F2) — współdzielone
   * z multi-add (`handlePickerConfirm` powyżej). Zachowanie pojedynczego
   * wyboru BEZ ZMIAN — zamienia produkt istniejącego wiersza na miejscu.
   * FIX F (przegląd F2): wywołujący wiersz mógł zostać usunięty, gdy picker
   * pozostał otwarty (`advancedPickerRowKey` trzyma STARY klucz) — zamiast
   * cicho gubić wybór usera, DOPISUJEMY towar jako NOWY wiersz (ten sam tryb
   * co multi-add w `handlePickerConfirm`). */
  function applyProduct(key: string, product: ProductPickResult | null) {
    if (!product) {
      updateRow(key, { productId: null });
      return;
    }
    const currentRow = value.find((row) => row.key === key);
    if (!currentRow) {
      onChange([...value, draftFromProduct(emptyDraft(), product, vatAllItems)]);
      return;
    }
    updateRow(key, draftFromProduct(currentRow, product, vatAllItems));
  }

  function onVatChange(key: string, code: string) {
    const match = vatAllItems.find((i) => i.code === code);
    updateRow(key, { vatRateCode: code, vatRate: match?.value ?? '0' });
  }

  /** Etykieta a11y uchwytu resize dla danej kolumny (mirror klucza filtra poniżej). */
  function resizeAriaLabel(col: ResolvedEmbeddedTableColumn): string {
    return t('tenant.documents.items.editor.resizeColumnAria', { column: columnLabelOf(col) });
  }

  /** Treść nagłówka danej kolumny — `unitPriceNet` niesie sufiks waluty (także przy własnym podpisie). */
  function renderHeaderLabel(colId: ColumnId, col: ResolvedEmbeddedTableColumn): React.ReactNode {
    const label = columnLabelOf(col);
    return colId === 'unitPriceNet' && currency !== 'PLN' ? `${label} (${currency})` : label;
  }

  /** Pojedyncza komórka ciała wiersza dla danej kolumny układu — pełny `<td>`. */
  function renderBodyCell(
    { colId, col }: EditorColumn,
    row: DocumentLineItemDraft,
    index: number,
    isInvalidRow: boolean,
    preview: ReturnType<typeof computeLinePreview>,
  ): React.ReactNode {
    const label = columnLabelOf(col);
    const align = effectiveColumnAlign(col);
    const alignClass = columnAlignClass(align);
    if (isExtColumnId(colId)) {
      const slug = extColumnSlug(colId);
      const field = extFieldBySlug.get(slug);
      if (!field) {
        // Pole ext skasowane po zapisaniu układu kolumn — deskryptory i ta mapa pochodzą z jednej
        // listy pól, więc dziś nieosiągalne; surowy slug zamiast crasha.
        return (
          <td
            key={colId}
            className={td}
            data-testid={`document-item-ext-placeholder-${slug}-${index}`}
          >
            <span className="text-xs text-text-tertiary">{slug}</span>
          </td>
        );
      }
      return (
        <td
          key={colId}
          className={cn(td, col.align !== undefined && EXT_INPUT_ALIGN_CLASS[col.align])}
        >
          <ExtFieldValueInput
            field={field}
            value={row.extValues[slug]}
            onChange={(v) => updateRow(row.key, { extValues: { ...row.extValues, [slug]: v } })}
            recordValues={row.extValues}
            valueAlign={col.align}
            aria-label={label}
            data-testid={`document-item-ext-${slug}-${index}`}
          />
        </td>
      );
    }
    switch (colId) {
      case 'lineNumber':
        return (
          <td
            key={colId}
            className={cn('px-2 py-1 align-top text-text-tertiary tabular-nums', alignClass)}
          >
            {index + 1}
          </td>
        );
      case 'product':
        return (
          // Wyrównanie z układu dosięga tekstu wybranego towaru: etykieta w pickerze to element
          // `flex-1` dziedziczący `text-align` po komórce (ikony i przyciski pickera zostają po bokach).
          <td key={colId} className={cn(td, alignClass)}>
            <ProductSearchPicker
              aria-label={label}
              value={row.productId}
              selectedLabel={row.name || null}
              companyId={companyId}
              onSelect={(product) => applyProduct(row.key, product)}
              onOpenAdvancedPicker={() => setAdvancedPickerRowKey(row.key)}
              data-testid={`document-item-product-${index}`}
            />
          </td>
        );
      case 'name':
        return (
          <td key={colId} className={td}>
            <Input
              size="sm"
              className={cn(
                alignClass,
                row.productId && tabs && 'underline decoration-dotted underline-offset-2',
              )}
              invalid={isInvalidRow}
              hideStatusIcon
              value={row.name}
              onChange={(e) => updateRow(row.key, { name: e.target.value })}
              onDoubleClick={
                row.productId && tabs
                  ? (e) => {
                      // Dwuklik na nazwie podpiętego towaru → kartoteka w nowej karcie
                      // powłoki (dokument zostaje w swojej). Pojedynczy klik = edycja.
                      // preventDefault: best-effort ograniczenie domyślnej akcji dwukliku
                      // (ewentualne zaznaczenie słowa zostaje w dokumencie w tle — nieszkodliwe).
                      e.preventDefault();
                      tabs.openTab(`/products/${row.productId}`);
                    }
                  : undefined
              }
              placeholder={label}
              aria-label={label}
              title={
                row.productId && tabs
                  ? t('tenant.documents.items.editor.openProductRecord')
                  : undefined
              }
              maxLength={512}
              data-testid={`document-item-name-${index}`}
            />
          </td>
        );
      case 'productCode':
        return (
          <td key={colId} className={td}>
            <Input
              size="sm"
              className={alignClass}
              value={row.productCode}
              onChange={(e) => updateRow(row.key, { productCode: e.target.value })}
              aria-label={label}
              maxLength={50}
              data-testid={`document-item-code-${index}`}
            />
          </td>
        );
      case 'unit':
        return (
          <td key={colId} className={td}>
            <Input
              size="sm"
              className={alignClass}
              value={row.unit}
              onChange={(e) => updateRow(row.key, { unit: e.target.value })}
              aria-label={label}
              maxLength={20}
              data-testid={`document-item-unit-${index}`}
            />
          </td>
        );
      case 'quantity':
        return (
          <td key={colId} className={td}>
            <Input
              size="sm"
              className={alignClass}
              invalid={isInvalidRow}
              hideStatusIcon
              inputMode="decimal"
              value={row.quantity}
              onChange={(e) =>
                updateRow(row.key, { quantity: normalizeDecimalInput(e.target.value) })
              }
              aria-label={label}
              data-testid={`document-item-quantity-${index}`}
            />
          </td>
        );
      case 'unitPriceNet':
        return (
          <td key={colId} className={td}>
            <Input
              size="sm"
              className={alignClass}
              invalid={isInvalidRow}
              hideStatusIcon
              inputMode="decimal"
              value={row.unitPriceNet}
              onChange={(e) =>
                updateRow(row.key, {
                  unitPriceNet: normalizeDecimalInput(e.target.value),
                })
              }
              aria-label={label}
              data-testid={`document-item-price-${index}`}
            />
          </td>
        );
      case 'discountPercent':
        return (
          <td key={colId} className={td}>
            <Input
              size="sm"
              className={alignClass}
              invalid={isInvalidRow}
              hideStatusIcon
              inputMode="decimal"
              value={row.discountPercent}
              onChange={(e) =>
                updateRow(row.key, {
                  discountPercent: normalizeDecimalInput(e.target.value),
                })
              }
              aria-label={label}
              data-testid={`document-item-discount-${index}`}
            />
          </td>
        );
      case 'vatRateCode':
        return (
          <td key={colId} className={td}>
            <Dropdown
              className={cn(isInvalidRow && invalidCellClass)}
              valueClassName={listControlValueAlignClass(col.align)}
              options={[
                { value: '', label: t('tenant.documents.items.editor.vatPlaceholder') },
                ...vatActiveItems.map((item) => ({ value: item.code, label: item.label })),
                // Kod z serwera/kartoteki nieobecny wśród aktywnych pozycji słownika
                // (nieaktywny/ukryty w międzyczasie) — syntetyczna opcja pokazuje
                // rzeczywisty kod zamiast pustego selecta (FIX 3).
                ...(row.vatRateCode && !vatActiveItems.some((item) => item.code === row.vatRateCode)
                  ? [{ value: row.vatRateCode, label: row.vatRateCode }]
                  : []),
              ]}
              value={row.vatRateCode}
              onChange={(v) => onVatChange(row.key, v)}
              aria-label={label}
              data-testid={`document-item-vat-${index}`}
            />
          </td>
        );
      case 'pkwiu':
        return (
          <td key={colId} className={td}>
            <Input
              size="sm"
              className={alignClass}
              value={row.pkwiu}
              onChange={(e) => updateRow(row.key, { pkwiu: e.target.value })}
              aria-label={label}
              maxLength={50}
              data-testid={`document-item-pkwiu-${index}`}
            />
          </td>
        );
      case 'cn':
        return (
          <td key={colId} className={td}>
            <Input
              size="sm"
              className={alignClass}
              value={row.cn}
              onChange={(e) => updateRow(row.key, { cn: e.target.value })}
              aria-label={label}
              maxLength={50}
              data-testid={`document-item-cn-${index}`}
            />
          </td>
        );
      case 'gtin':
        return (
          <td key={colId} className={td}>
            <Input
              size="sm"
              className={alignClass}
              value={row.gtin}
              onChange={(e) => updateRow(row.key, { gtin: e.target.value })}
              aria-label={label}
              maxLength={20}
              data-testid={`document-item-gtin-${index}`}
            />
          </td>
        );
      case 'valueNet':
        return (
          <td
            key={colId}
            className={cn(tdComputed, alignClass)}
            data-testid={`document-item-preview-net-${index}`}
          >
            {formatMoney(preview.valueNet, currency, col.decimals)}
          </td>
        );
      case 'valueVat':
        return (
          <td
            key={colId}
            className={cn(tdComputed, alignClass)}
            data-testid={`document-item-preview-vat-${index}`}
          >
            {formatMoney(preview.valueVat, currency, col.decimals)}
          </td>
        );
      case 'valueGross':
        return (
          <td
            key={colId}
            className={cn(tdComputed, alignClass)}
            data-testid={`document-item-preview-gross-${index}`}
          >
            {formatMoney(preview.valueGross, currency, col.decimals)}
          </td>
        );
      default:
        return null;
    }
  }

  /** Komórka akcji (usunięcie wiersza) — zawsze ostatnia, poza układem kolumn. */
  function renderActionsCell(row: DocumentLineItemDraft, index: number): React.ReactNode {
    return (
      <td key="actions" className="px-2 py-1 align-top text-center">
        <IconButton
          variant="danger"
          size="sm"
          onClick={() => removeRow(row.key)}
          aria-label={t('tenant.documents.items.editor.delete', { row: index + 1 })}
          data-testid={`document-item-remove-${index}`}
        >
          <Trash2 className="h-4 w-4" />
        </IconButton>
      </td>
    );
  }

  // Kolumny układu + kolumna akcji na końcu.
  const totalColumnCount = columns.length + 1;

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="overflow-x-auto rounded border border-border-primary">
        <table className="w-full table-fixed text-sm">
          <colgroup>
            {columns.map((column) => (
              <col
                key={column.colId}
                data-testid={`document-items-col-${column.colId}`}
                style={{ width: `${widthOf(column)}px` }}
              />
            ))}
            <col
              data-testid="document-items-col-actions"
              style={{ width: `${ACTIONS_COLUMN_WIDTH}px` }}
            />
          </colgroup>
          <thead>
            <tr className="border-b border-border-primary bg-bg-secondary">
              {columns.map((column) => (
                <th
                  key={column.colId}
                  className={cn(
                    thBase,
                    columnAlignClass(effectiveColumnAlign(column.col)),
                    'relative',
                  )}
                  scope="col"
                >
                  {renderHeaderLabel(column.colId, column.col)}
                  <ColumnResizeHandle
                    colId={column.colId}
                    width={widthOf(column)}
                    isActive={resizingColumn === column.colId}
                    label={resizeAriaLabel(column.col)}
                    onPointerDownStart={handleResizeStart}
                  />
                </th>
              ))}
              <th className={cn(thBase, 'relative text-left')} scope="col">
                <span className="sr-only">{t(COLUMN_HEADER_KEYS.actions)}</span>
              </th>
            </tr>
            <tr className="border-b border-border-primary bg-bg-secondary/60">
              {columns.map(({ colId, col }) => (
                <ColumnFilterCell
                  key={colId}
                  colId={colId}
                  value={filters[colId] ?? ''}
                  ariaLabel={t('tenant.documents.items.editor.filterAriaLabel', {
                    column: columnLabelOf(col),
                  })}
                  onChangeValue={setColumnFilter}
                />
              ))}
              <th className="px-2 py-1" scope="col" />
            </tr>
          </thead>
          <tbody>
            {value.length === 0 ? (
              <tr>
                <td
                  className="px-3 py-6 text-center text-sm text-text-tertiary"
                  colSpan={totalColumnCount}
                >
                  {t('tenant.documents.items.editor.emptyHint')}
                </td>
              </tr>
            ) : visibleRows.length === 0 ? (
              <tr>
                <td
                  className="px-3 py-6 text-center text-sm text-text-tertiary"
                  colSpan={totalColumnCount}
                  data-testid="document-items-filter-no-matches"
                >
                  {t('tenant.documents.items.editor.filterNoMatches')}
                </td>
              </tr>
            ) : (
              visibleRows.map(({ row, originalIndex }) => {
                const index = originalIndex;
                const preview = computeLinePreview(row);
                const isInvalidRow = invalidRowKeys.has(row.key);
                return (
                  <tr
                    key={row.key}
                    className="border-b border-border-primary last:border-0"
                    data-testid={`document-item-edit-row-${index}`}
                  >
                    {columns.map((column) =>
                      renderBodyCell(column, row, index, isInvalidRow, preview),
                    )}
                    {renderActionsCell(row, index)}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="ghost" onClick={addRow} data-testid="document-items-add-row">
          <Plus className="mr-1 h-4 w-4" />
          {t('tenant.documents.items.editor.addRow')}
        </Button>

        <div
          className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm"
          data-testid="document-items-preview-totals"
        >
          <span className="text-xs uppercase tracking-wide text-text-tertiary">
            {t('tenant.documents.items.editor.previewNote')}
          </span>
          <span className="text-text-secondary">
            {t('tenant.documents.items.totals.net', { currency })}:{' '}
            <span className="font-semibold tabular-nums text-text-primary">
              {formatMoney(totals.valueNet, currency)}
            </span>
          </span>
          <span className="text-text-secondary">
            {t('tenant.documents.items.totals.vat', { currency })}:{' '}
            <span className="font-semibold tabular-nums text-text-primary">
              {formatMoney(totals.valueVat, currency)}
            </span>
          </span>
          <span className="text-text-secondary">
            {t('tenant.documents.items.totals.gross', { currency })}:{' '}
            <span className="font-semibold tabular-nums text-text-primary">
              {formatMoney(totals.valueGross, currency)}
            </span>
          </span>
        </div>
      </div>

      <ProductPickerModal
        open={advancedPickerRowKey !== null}
        onClose={() => setAdvancedPickerRowKey(null)}
        companyId={companyId}
        onConfirm={handlePickerConfirm}
      />
    </div>
  );
}
